import { createClient, processLock } from '@supabase/supabase-js';

import type { Database as SupabaseDatabase } from '@/integrations/supabase/types';
import { captureEmailLinkError } from '@/lib/auth/pending-email-confirmation';
import { logger } from '@/utils/logger';

const getEnvVar = (key: string, defaultValue: string = ''): string => {
  try {
    return import.meta.env[key] || defaultValue;
  } catch {
    return defaultValue;
  }
};

const supabaseUrl = getEnvVar('VITE_SUPABASE_URL', 'https://temp.supabase.co');
const supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY', 'temp-anon-key');

if (typeof window !== 'undefined') {
  captureEmailLinkError(window.sessionStorage, window.location.hash, window.location.search);
}

export type Database = SupabaseDatabase;

if (!supabaseUrl || supabaseUrl === 'https://temp.supabase.co') {
  logger.warn('Warning: VITE_SUPABASE_URL is not set. Supabase client may not work correctly.');
}
if (!supabaseAnonKey || supabaseAnonKey === 'temp-anon-key') {
  logger.warn(
    'Warning: VITE_SUPABASE_ANON_KEY is not set. Supabase client may not work correctly.',
  );
}

// Cache the Supabase client across Vite HMR module reloads. Without this guard,
// every edit to a file in the import graph re-executes this module and creates
// a NEW client. The old client still holds the auth-token Web Lock, so the new
// client times out trying to acquire it (5s+), and every authenticated request
// fails with "All authentication methods failed". Production has no HMR so this
// branch is irrelevant there; the module-level singleton is enough.
type CachedSupabase = { __moraSupabase?: ReturnType<typeof createClient<Database>> };
const cache = typeof window !== 'undefined' ? (window as unknown as CachedSupabase) : {};

export const supabase =
  cache.__moraSupabase ??
  createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // processLock = in-process promise queue. Default is navigatorLock (Web
      // Locks API) which serializes auth-token refreshes ACROSS browser tabs
      // AND across React Strict Mode double-mounts. In dev, Strict Mode +
      // many parallel queries on first load creates lock contention so severe
      // that requests time out with "All authentication methods failed". The
      // process-local lock keeps refresh serialization without the cross-tab
      // / cross-mount starvation. See @supabase/auth-js/lib/locks.
      lock: processLock,
    },
    global: {
      headers: {
        'X-Client-Info': 'mora-web-client',
      },
    },
    db: {
      schema: 'public',
    },
  });

if (typeof window !== 'undefined') {
  cache.__moraSupabase = supabase;
  // DEV-ONLY: expose the client on window so the dev-session-issuer script can
  // call `window.__moraSupabase.auth.setSession({access_token, refresh_token})`
  // from the browser console to bypass Turnstile captcha during E2E testing.
  // This is a no-op in prod builds (import.meta.env.DEV = false → pruned).
  if (import.meta.env.DEV) {
    (window as unknown as { __moraSupabase: typeof supabase }).__moraSupabase = supabase;
  }

  // Sync auth token to the Realtime WebSocket on every auth state change
  // (sign-in, token refresh, sign-out). Without this, the WebSocket uses
  // the stale anon key after the initial connection and fails with
  // "HTTP Authentication failed; no valid credentials available" (Bug #3).
  //
  // F-120 (UAT 2026-06-14): onAuthStateChange fires for MANY events that don't
  // change the token — INITIAL_SESSION, USER_UPDATED, and (since F-107 added
  // visibility/focus refresh) every tab refocus. Calling realtime.setAuth on
  // each one re-pushes auth to the socket and can cycle a still-connecting
  // socket ("WebSocket is closed before the connection is established" churn).
  // Dedupe so we only re-auth on an ACTUAL token change. (Remaining churn on a
  // dropping connection is Phoenix's network-level reconnect, not an app loop.)
  let lastRealtimeToken: string | null | undefined;
  supabase.auth.onAuthStateChange((_event, session) => {
    const token = session?.access_token ?? null;
    if (token === lastRealtimeToken) return;
    lastRealtimeToken = token;
    supabase.realtime.setAuth(token);
  });
}

export let devAuthReady: Promise<void> = Promise.resolve();

/** Local Supabase CLI uses 127.0.0.1 or localhost — both must bootstrap a real JWT for RLS. */
const isLocalSupabaseUrl = supabaseUrl.includes('127.0.0.1') || supabaseUrl.includes('localhost');

// `typeof window` guard ensures Node runtimes (Vercel Workflow DevKit, edge
// fns) don't reach into `import.meta.env` which the bundler replaces with
// `{}` for CJS — accessing `.DEV` on undefined throws and crashes module
// load before any step runs. Bug #4 root cause.
if (typeof window !== 'undefined' && import.meta.env.DEV && isLocalSupabaseUrl) {
  devAuthReady = supabase.auth.getSession().then(async ({ data }) => {
    if (!data.session) {
      await supabase.auth.signInWithPassword({
        email: 'test@example.com',
        password: 'password123',
      });
    }
  });
}
